<section class="flex flex-col gap-2" [attr.data-testid]="'address-transport-routes-' + role()">
  <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
    <span class="font-semibold">{{ title() | translate }}</span>
    <span class="text-muted-color">{{ hint() | translate }}</span>
    <p-button
      class="ms-auto"
      icon="fa fa-plus"
      size="small"
      [outlined]="true"
      [label]="'transport-route.address.add' | translate"
      [disabled]="loading() || unusedRoutes().length === 0"
      [pTooltip]="unusedRoutes().length === 0 && catalog().length > 0 ? ('transport-route.address.all-used' | translate) : ''"
      (onClick)="addRow()"
      [attr.data-testid]="'address-transport-routes-add-' + role()"
    />
  </div>

  @if (error(); as message) {
    <p-message severity="error" [closable]="true" (onClose)="error.set(null)">{{ message }}</p-message>
  }
  @if (!loading() && catalog().length === 0) {
    <p-message severity="info">
      {{ 'transport-route.address.empty-catalog' | translate }}
      <a routerLink="/transport-route" class="ms-1 text-primary underline-offset-2 hover:underline">{{ 'constants.transport-route' | translate }}</a>
    </p-message>
  }

  <p-table [value]="rows()" dataKey="key" [rowTrackBy]="trackByKey" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '52rem' }" [loading]="loading()">
    <ng-template #header>
      <tr>
        <th style="min-width: 16rem">{{ 'entities.AddressTransportRoute.transportRoute' | translate }}</th>
        <th class="w-32 text-end" [pTooltip]="'transport-route.address.transit-tooltip' | translate">{{ 'entities.AddressTransportRoute.transitDays' | translate }}</th>
        <th class="w-36 text-end">{{ 'entities.AddressTransportRoute.fixedCost' | translate }}</th>
        <th class="w-36 text-end">{{ 'entities.AddressTransportRoute.costPerUnit' | translate }}</th>
        <th class="w-20 text-center">{{ 'entities.AddressTransportRoute.isDefault' | translate }}</th>
        <th>{{ 'entities.AddressTransportRoute.notes' | translate }}</th>
        <th class="w-28"></th>
      </tr>
    </ng-template>
    <ng-template #body let-row>
      @let busy = busyKey() === row.key;
      @let route = catalogOf(row);
      <tr [attr.data-testid]="'address-transport-route-row-' + (row.saved ? route?.identifier : 'new')">
        <td>
          @if (row.saved) {
            <span class="inline-flex items-center gap-2">
              <i [class]="iconOf(row)" class="text-muted-color"></i>
              <span class="font-medium">{{ route?.identifier }}</span>
              <span class="text-muted-color">{{ route?.description }}</span>
            </span>
          } @else {
            <p-select
              [options]="routeOptions(row)"
              optionLabel="identifier"
              optionValue="id"
              [ngModel]="row.transportRouteId"
              (ngModelChange)="update(row, { transportRouteId: $event })"
              size="small"
              appendTo="body"
              styleClass="w-full"
              [attr.data-testid]="'address-transport-route-select'"
            >
              <ng-template #selectedItem let-item>
                @if (item) {
                  <span class="inline-flex items-center gap-2"><i [class]="routeIcon(item)"></i>{{ item.identifier }}</span>
                }
              </ng-template>
              <ng-template #item let-item>
                <span class="inline-flex items-center gap-2"><i [class]="routeIcon(item)"></i><span class="font-medium">{{ item.identifier }}</span><span class="text-muted-color">{{ item.description }}</span></span>
              </ng-template>
            </p-select>
          }
        </td>
        <td class="text-end">
          <p-inputnumber
            [ngModel]="row.transitDays"
            (ngModelChange)="update(row, { transitDays: $event })"
            [min]="0"
            [maxFractionDigits]="0"
            [locale]="locale()"
            [placeholder]="route?.transitDays != null ? '' + route?.transitDays : ''"
            size="small"
            inputStyleClass="w-24 text-right"
            [attr.data-testid]="'address-transport-route-transit'"
          />
        </td>
        <td class="text-end">
          <p-inputnumber
            [ngModel]="row.fixedCost"
            (ngModelChange)="update(row, { fixedCost: $event })"
            [min]="0"
            [minFractionDigits]="2"
            [maxFractionDigits]="4"
            mode="currency"
            [currency]="currencyCode()"
            [locale]="locale()"
            size="small"
            inputStyleClass="w-28 text-right"
            [attr.data-testid]="'address-transport-route-fixed'"
          />
        </td>
        <td class="text-end">
          <p-inputnumber
            [ngModel]="row.costPerUnit"
            (ngModelChange)="update(row, { costPerUnit: $event })"
            [min]="0"
            [minFractionDigits]="2"
            [maxFractionDigits]="4"
            mode="currency"
            [currency]="currencyCode()"
            [locale]="locale()"
            size="small"
            inputStyleClass="w-28 text-right"
            [attr.data-testid]="'address-transport-route-per-unit'"
          />
        </td>
        <td class="text-center">
          <button
            type="button"
            class="h-7 w-7 rounded hover:bg-surface-200 dark:hover:bg-surface-700 disabled:cursor-default"
            [disabled]="!row.saved || busy"
            [pTooltip]="(row.saved?.isDefault ? 'transport-route.address.is-default' : 'transport-route.address.set-default') | translate"
            (click)="setDefault(row)"
            [attr.data-testid]="'address-transport-route-default'"
          >
            <i class="fa fa-star" [class.text-amber-500]="row.saved?.isDefault" [class.text-surface-300]="!row.saved?.isDefault"></i>
          </button>
        </td>
        <td>
          <input
            pInputText
            type="text"
            class="w-full p-inputtext-sm"
            [ngModel]="row.notes"
            (ngModelChange)="update(row, { notes: $event })"
            [attr.data-testid]="'address-transport-route-notes'"
          />
        </td>
        <td class="whitespace-nowrap text-end">
          @if (isDirty(row)) {
            <p-button icon="fa fa-check" severity="success" size="small" [text]="true" [rounded]="true" [loading]="busy" [disabled]="!row.transportRouteId" tooltipPosition="left" [pTooltip]="'common.save' | translate" (onClick)="saveRow(row)" [attr.data-testid]="'address-transport-route-save'" />
            <p-button icon="fa fa-rotate-left" severity="secondary" size="small" [text]="true" [rounded]="true" [disabled]="busy" tooltipPosition="left" [pTooltip]="'transport-route.address.revert' | translate" (onClick)="revertRow(row)" [attr.data-testid]="'address-transport-route-revert'" />
          }
          @if (row.saved) {
            <p-button icon="fa fa-trash" severity="danger" size="small" [text]="true" [rounded]="true" [disabled]="busy" tooltipPosition="left" [pTooltip]="'common.delete' | translate" (onClick)="deleteRow(row)" [attr.data-testid]="'address-transport-route-delete'" />
          }
        </td>
      </tr>
    </ng-template>
    <ng-template #emptymessage>
      <tr>
        <td colspan="7" class="text-sm text-muted-color">{{ 'transport-route.address.none' | translate }}</td>
      </tr>
    </ng-template>
  </p-table>
</section>
